/**
 * Kompakte Terminliste der Uebersicht (V-01). Ein Klick oeffnet den Termin im
 * Kalender (Navigation, V-02); Schulstunden stehen nur als eine Zeile da.
 */
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';

import { useAreaColor, useAreaMap } from '@/modules/areas/useAreas';
import { AreaDot } from '@/modules/areas/AreaDot';
import { useCalendarStore } from '@/store/calendar';
import { useUiStore } from '@/store/ui';
import { datePart, formatDe } from '@/lib/datetime';
import type { TerminListe } from '@/domain/uebersicht';
import type { CalendarOccurrence, LocalDate } from '@/domain/types';

/** So viele Termine stehen direkt da. */
const MAX = 8;

export function TerminZeilen({ liste, leer, mitDatum = false }: {
  liste: TerminListe;
  leer: string;
  /** Mehrere Tage: Wochentag und Datum vor jedem Termin. */
  mitDatum?: boolean;
}) {
  const navigate = useNavigate();
  const areaColor = useAreaColor();
  const areaMap = useAreaMap();
  const setView = useUiStore((s) => s.setCalendarView);
  const setAnchor = useCalendarStore((s) => s.setAnchor);
  const select = useCalendarStore((s) => s.select);

  function oeffnen(o: CalendarOccurrence) {
    setView('tag');
    setAnchor(datePart(String(o.start) as LocalDate));
    select(o);
    navigate('/kalender');
  }

  const { termine, schule } = liste;
  if (termine.length === 0 && !schule) return <p className="text-sm text-ink-faint">{leer}</p>;

  const zeit = (o: CalendarOccurrence) => {
    const muster = mitDatum ? (o.allDay ? 'EEEEEE dd.MM.' : 'EEEEEE dd.MM. HH:mm') : 'HH:mm';
    return !mitDatum && o.allDay ? 'ganztags' : formatDe(String(o.start), muster);
  };

  return (
    <ul className="flex flex-col">
      {termine.slice(0, MAX).map((o) => (
        <li key={o.occurrenceId}>
          <button
            type="button"
            onClick={() => oeffnen(o)}
            className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left text-sm hover:bg-surface-raised"
          >
            <span className={mitDatum ? 'w-[7.5rem] shrink-0 tabular-nums text-ink-faint' : 'w-14 shrink-0 tabular-nums text-ink-faint'}>{zeit(o)}</span>
            <AreaDot color={areaColor(o.areaId)} size="sm" name={areaMap.get(o.areaId ?? '')?.name} />
            <span className="min-w-0 flex-1 truncate">{o.title || 'Ohne Titel'}</span>
          </button>
        </li>
      ))}
      {termine.length > MAX && <li className="px-1 text-[11px] text-ink-faint">+{termine.length - MAX} weitere</li>}
      {schule && (
        <li>
          <Link to="/schule" className="flex items-center gap-2 rounded px-1 py-0.5 text-sm text-ink-muted hover:bg-surface-raised hover:text-ink">
            <GraduationCap className="size-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
            {mitDatum
              ? `Schule: ${schule.anzahl} ${schule.anzahl === 1 ? 'Stunde' : 'Stunden'}`
              : `Schule: ${schule.anzahl} ${schule.anzahl === 1 ? 'Stunde' : 'Stunden'}, ${schule.von}–${schule.bis}`}
          </Link>
        </li>
      )}
    </ul>
  );
}
